import type { Place, TastingNote, Visit } from '@recipes/contracts';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Spinner,
  StarRating,
  Switch,
  Textarea,
} from '@recipes/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowDown,
  ArrowRightLeft,
  ArrowUp,
  ExternalLink,
  MapPin,
  Merge,
  Pencil,
  Plus,
  Sparkles,
  Trash2,
  Users,
} from 'lucide-react';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';
import { toast } from '@/components/feedback/toaster';
import { ConfirmDialog } from '@/features/shared/confirm-dialog';
import { useAiConsent, useFeature } from '@/features/shared/hooks';
import { FeaturePageHeader, PageContainer } from '@/features/shared/page-header';
import type { DishDraft } from '@/features/tasting/dish-draft';
import { uploadTastingPhoto } from '@/features/tasting/capture-queue';
import { DishFields } from '@/features/tasting/dish-fields';
import { formatDateTime, placeSubtitle } from '@/features/tasting/format';
import { LazyTastingMap } from '@/features/tasting/map/lazy-map';
import { PlacePicker } from '@/features/tasting/place-picker';
import { MergeVisitDialog, MoveDishDialog } from '@/features/tasting/visit-tools';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth/auth-provider';
import { describeError, isVersionConflict, toastError } from '@/lib/errors';
import { queryKeys } from '@/lib/query-keys';

function useVisitCache(visitId: string) {
  const queryClient = useQueryClient();
  return (visit: Visit | null) => {
    if (visit) queryClient.setQueryData(queryKeys.tasting.visit(visitId), visit);
    void queryClient.invalidateQueries({ queryKey: queryKeys.tasting.visitsAll });
  };
}

function PlaceCard({ place, visit }: { place: Place; visit: Visit }) {
  const { t } = useTranslation();
  return (
    <Card>
      <CardContent className="space-y-3 p-4">
        <div className="flex flex-wrap items-start gap-3">
          <MapPin className="mt-0.5 size-5 shrink-0 text-primary" aria-hidden />
          <div className="min-w-0 flex-1">
            <h2 className="font-semibold">
              {place.own ? (
                <Link to={`/tasting/places/${place.id}`} className="hover:underline">
                  {place.name}
                </Link>
              ) : (
                place.name
              )}
            </h2>
            <p className="text-sm text-muted-foreground">{placeSubtitle(place)}</p>
            {place.website && (
              <a
                href={place.website}
                target="_blank"
                rel="noreferrer noopener"
                className="mt-1 inline-flex items-center gap-1 text-sm text-primary underline"
              >
                {t('tasting.visit.website')} <ExternalLink className="size-3" aria-hidden />
              </a>
            )}
          </div>
        </div>
        <LazyTastingMap
          ariaLabel={t('tasting.visit.mapLabel', { place: place.name })}
          points={[{ id: visit.id, lat: place.lat, lng: place.lng, label: place.name }]}
          center={{ lat: place.lat, lng: place.lng }}
          zoom={16}
          interactive={false}
          className="h-44 w-full overflow-hidden rounded-lg border"
        />
      </CardContent>
    </Card>
  );
}

function EditNoteDialog({
  note,
  canAddPhotos,
  onClose,
  onSaved,
}: {
  note: TastingNote;
  /** A photo belongs to whoever uploads it, so only the author of the dish may add some. */
  canAddPhotos: boolean;
  onClose: () => void;
  onSaved: (visit: Visit) => void;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<DishDraft>({
    dishName: note.dishName ?? '',
    notes: note.notes,
    flavorTags: note.flavorTags,
    rating: note.rating,
    photos: [],
  });
  const [keptPhotos, setKeptPhotos] = useState(note.photos);
  const update = useMutation({
    mutationFn: async () => {
      const added: Array<{ assetId: string; altText: string | null }> = [];
      for (const photo of draft.photos) {
        const altText = draft.dishName.trim() || null;
        const assetId = await uploadTastingPhoto({
          blob: photo.file,
          name: photo.file.name,
          mimeType: photo.file.type,
          altText,
        });
        added.push({ assetId, altText });
      }
      return api.tastingNotes.update(note.id, {
        version: note.version,
        dishName: draft.dishName.trim() || null,
        notes: draft.notes.trim(),
        flavorTags: draft.flavorTags,
        rating: draft.rating,
        photos: [...keptPhotos.map((p) => ({ assetId: p.assetId, altText: p.altText })), ...added],
      });
    },
    onSuccess: (visit) => {
      toast.success(t('tasting.visit.dishSaved'));
      onSaved(visit);
      onClose();
    },
    onError: (e) =>
      isVersionConflict(e) ? toast.error(t('tasting.visit.conflict')) : toastError(e),
  });
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('tasting.visit.editDish')}</DialogTitle>
          <DialogDescription>{t('tasting.visit.editDishHint')}</DialogDescription>
        </DialogHeader>
        {keptPhotos.length > 0 && (
          <ul className="grid grid-cols-4 gap-2">
            {keptPhotos.map((photo, index) => (
              <li key={photo.assetId} className="relative">
                <img
                  src={photo.thumbnailUrl ?? photo.url ?? ''}
                  alt={photo.altText ?? t('tasting.dish.photoAlt', { index: index + 1 })}
                  className="aspect-square w-full rounded-md object-cover"
                />
                <Button
                  type="button"
                  size="icon"
                  variant="secondary"
                  className="absolute right-1 top-1 size-9"
                  aria-label={t('tasting.dish.removePhoto', { index: index + 1 })}
                  onClick={() =>
                    setKeptPhotos(keptPhotos.filter((p) => p.assetId !== photo.assetId))
                  }
                >
                  <Trash2 aria-hidden />
                </Button>
              </li>
            ))}
          </ul>
        )}
        <DishFields
          value={draft}
          onChange={setDraft}
          allowPhotos={canAddPhotos}
          existingPhotoCount={keptPhotos.length}
        />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button loading={update.isPending} onClick={() => update.mutate()}>
            {t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DishCard({
  visit,
  note,
  index,
  onChanged,
  onReorder,
  reordering,
}: {
  visit: Visit;
  note: TastingNote;
  index: number;
  onChanged: (visit: Visit | null) => void;
  onReorder: (from: number, to: number) => void;
  reordering: boolean;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const aiEnabled = useFeature('ai');
  const consented = useAiConsent();
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [consentOpen, setConsentOpen] = useState(false);
  const [moving, setMoving] = useState(false);
  const { user } = useAuth();
  const canManage = visit.permissions.canManage;
  const dishLabel = note.dishName ?? t('tasting.visit.unnamedDish');

  const remove = useMutation({
    mutationFn: () => api.tastingNotes.remove(note.id),
    onSuccess: ({ visit: remaining }) => {
      toast.success(t('tasting.visit.dishDeleted'));
      onChanged(remaining);
      if (!remaining) navigate('/tasting');
    },
    onError: (e) => toastError(e),
  });
  const suggest = useMutation({
    mutationFn: () => api.tastingNotes.suggestRecipes(note.id, {}, crypto.randomUUID()),
    onSuccess: (generation) =>
      navigate(`/tasting/notes/${note.id}/suggestions/${generation.id}?visit=${visit.id}`),
    onError: (e) => toastError(e),
  });

  return (
    <Card data-testid="dish-card">
      <CardContent className="space-y-3 p-4">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="text-lg font-semibold break-words">
              {note.dishName ?? t('tasting.visit.unnamedDish')}
            </h3>
            {note.authorId !== visit.ownerId && (
              <p className="text-xs text-muted-foreground">
                {t('tasting.visit.addedBy', {
                  name: note.authorName ?? t('tasting.visit.formerMember'),
                })}
              </p>
            )}
          </div>
          {note.rating !== null && (
            <StarRating value={note.rating} readOnly label={t('tasting.dish.rating')} />
          )}
        </div>

        {note.photos.length > 0 && (
          <ul className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {note.photos.map((photo, index) => (
              <li key={photo.assetId}>
                {photo.url ? (
                  <a href={photo.url} target="_blank" rel="noreferrer">
                    <img
                      src={photo.url}
                      alt={photo.altText ?? t('tasting.dish.photoAlt', { index: index + 1 })}
                      loading="lazy"
                      className="aspect-square w-full rounded-md object-cover"
                    />
                  </a>
                ) : (
                  <span className="flex aspect-square items-center justify-center rounded-md bg-muted text-xs text-muted-foreground">
                    {t('tasting.visit.photoProcessing')}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}

        {note.notes && <p className="whitespace-pre-wrap text-sm">{note.notes}</p>}
        {note.flavorTags.length > 0 && (
          <ul className="flex flex-wrap gap-1" aria-label={t('tasting.dish.flavorTags')}>
            {note.flavorTags.map((tag) => (
              <li key={tag}>
                <Badge variant="secondary">{tag}</Badge>
              </li>
            ))}
          </ul>
        )}

        {note.recipes.length > 0 && (
          <div>
            <h4 className="text-sm font-medium">{t('tasting.visit.linkedRecipes')}</h4>
            <ul className="mt-1 space-y-1">
              {note.recipes.map((r) => (
                <li key={r.recipeId}>
                  <Link to={`/recipes/${r.recipeId}`} className="text-sm text-primary underline">
                    {r.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          {aiEnabled && visit.permissions.canSuggestRecipes && (
            <Button
              size="sm"
              loading={suggest.isPending}
              onClick={() => (consented ? setConsentOpen(true) : navigate('/profile'))}
              data-testid="suggest-recipes"
            >
              <Sparkles aria-hidden />
              {consented ? t('tasting.suggest.action') : t('tasting.suggest.needsConsent')}
            </Button>
          )}
          {note.canEdit && (
            <>
              <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
                <Pencil aria-hidden /> {t('common.edit')}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setDeleting(true)}>
                <Trash2 aria-hidden /> {t('common.delete')}
              </Button>
            </>
          )}
          {canManage && (
            <>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setMoving(true)}
                data-testid="move-dish"
              >
                <ArrowRightLeft aria-hidden /> {t('tasting.tools.moveAction')}
              </Button>
              {visit.notes.length > 1 && (
                <span className="ml-auto inline-flex gap-1">
                  <Button
                    size="icon"
                    variant="ghost"
                    disabled={index === 0 || reordering}
                    aria-label={t('tasting.tools.moveUp', { dish: dishLabel })}
                    onClick={() => onReorder(index, index - 1)}
                    data-testid="dish-up"
                  >
                    <ArrowUp aria-hidden />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    disabled={index === visit.notes.length - 1 || reordering}
                    aria-label={t('tasting.tools.moveDown', { dish: dishLabel })}
                    onClick={() => onReorder(index, index + 1)}
                    data-testid="dish-down"
                  >
                    <ArrowDown aria-hidden />
                  </Button>
                </span>
              )}
            </>
          )}
        </div>
      </CardContent>

      {editing && (
        <EditNoteDialog
          note={note}
          canAddPhotos={note.authorId === user?.id}
          onClose={() => setEditing(false)}
          onSaved={onChanged}
        />
      )}
      {moving && (
        <MoveDishDialog
          visit={visit}
          note={note}
          onClose={() => setMoving(false)}
          onMoved={(target) => {
            setMoving(false);
            onChanged(null);
            navigate(`/tasting/visits/${target.id}`);
          }}
        />
      )}
      <ConfirmDialog
        open={deleting}
        onOpenChange={setDeleting}
        title={t('tasting.visit.deleteDishTitle')}
        description={
          visit.notes.length === 1
            ? t('tasting.visit.deleteLastDish')
            : t('tasting.visit.deleteDishBody')
        }
        confirmLabel={t('common.delete')}
        destructive
        loading={remove.isPending}
        onConfirm={() => remove.mutate()}
      />
      {/* What exactly leaves the app is listed before anything is sent (spec 5.7). */}
      <ConfirmDialog
        open={consentOpen}
        onOpenChange={setConsentOpen}
        title={t('tasting.suggest.consentTitle')}
        confirmLabel={t('tasting.suggest.consentConfirm')}
        loading={suggest.isPending}
        onConfirm={() => suggest.mutate()}
      >
        <p className="text-sm">{t('tasting.suggest.consentIntro')}</p>
        <ul className="mt-2 list-disc space-y-1 pl-5 text-sm">
          {note.photos.length > 0 && (
            <li>{t('tasting.suggest.sendsPhotos', { count: note.photos.length })}</li>
          )}
          <li>{t('tasting.suggest.sendsNotes')}</li>
          {visit.notes.length > 1 && <li>{t('tasting.suggest.sendsOtherDishes')}</li>}
          {visit.place && (
            <li>
              {t('tasting.suggest.sendsPlace', {
                place: [visit.place.name, visit.place.city, visit.place.countryCode]
                  .filter(Boolean)
                  .join(', '),
              })}
            </li>
          )}
        </ul>
        <p className="mt-2 text-sm font-medium">{t('tasting.suggest.neverSends')}</p>
      </ConfirmDialog>
    </Card>
  );
}

/** A date as the value of a `datetime-local` field: local time, to the minute. */
function toLocalInput(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function TastingVisitDetailPage() {
  const { visitId = '' } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const commentId = useId();
  const visitedAtId = useId();
  const shareId = useId();
  const applyVisit = useVisitCache(visitId);
  const [choosingPlace, setChoosingPlace] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [comment, setComment] = useState<string | null>(null);
  // The value of the date field while it is being changed; null = as stored.
  const [visitedAt, setVisitedAt] = useState<string | null>(null);
  const [merging, setMerging] = useState(false);

  const query = useQuery({
    queryKey: queryKeys.tasting.visit(visitId),
    queryFn: () => api.visits.get(visitId),
    enabled: Boolean(visitId),
  });
  const visit = query.data;

  const update = useMutation({
    mutationFn: (patch: Omit<Parameters<typeof api.visits.update>[1], 'version'>) =>
      api.visits.update(visitId, { ...patch, version: visit!.version }),
    onSuccess: (next) => {
      applyVisit(next);
      setChoosingPlace(false);
      setComment(null);
      setVisitedAt(null);
    },
    onError: (e) => {
      if (isVersionConflict(e)) {
        toast.error(t('tasting.visit.conflict'));
        void query.refetch();
      } else toastError(e);
    },
  });
  const reorder = useMutation({
    mutationFn: ({ from, to }: { from: number; to: number }) => {
      const ids = visit!.notes.map((n) => n.id);
      const [moved] = ids.splice(from, 1);
      ids.splice(to, 0, moved!);
      return api.visits.reorderNotes(visitId, { version: visit!.version, noteIds: ids });
    },
    onSuccess: (next) => applyVisit(next),
    onError: (e) => {
      if (isVersionConflict(e)) {
        toast.error(t('tasting.visit.conflict'));
        void query.refetch();
      } else toastError(e);
    },
  });
  const remove = useMutation({
    mutationFn: () => api.visits.remove(visitId),
    onSuccess: () => {
      applyVisit(null);
      toast.success(t('tasting.visit.deleted'));
      navigate('/tasting');
    },
    onError: (e) => toastError(e),
  });

  if (query.isLoading)
    return (
      <PageContainer>
        <Spinner label={t('app.loading')} />
      </PageContainer>
    );
  if (query.error || !visit)
    return (
      <PageContainer className="max-w-3xl">
        <FeaturePageHeader title={t('tasting.title')} backTo="/tasting" />
        <Alert variant="destructive">{describeError(query.error)}</Alert>
      </PageContainer>
    );

  const canManage = visit.permissions.canManage;
  const position =
    visit.lat !== null && visit.lng !== null ? { lat: visit.lat, lng: visit.lng } : null;

  return (
    <PageContainer className="max-w-3xl">
      <FeaturePageHeader
        title={visit.place?.name ?? t('tasting.visit.unknownPlace')}
        description={formatDateTime(visit.visitedAt)}
        backTo="/tasting"
        actions={
          <>
            {visit.permissions.canAddNotes && (
              <Button asChild>
                <Link to={`/tasting/new?visit=${visit.id}`} data-testid="add-dish">
                  <Plus aria-hidden /> {t('tasting.visit.addDish')}
                </Link>
              </Button>
            )}
            {canManage && (
              <>
                <Button
                  variant="outline"
                  onClick={() => setMerging(true)}
                  data-testid="merge-visit"
                >
                  <Merge aria-hidden /> {t('tasting.tools.mergeAction')}
                </Button>
                <Button variant="ghost" onClick={() => setDeleting(true)}>
                  <Trash2 aria-hidden /> {t('common.delete')}
                </Button>
              </>
            )}
          </>
        }
      >
        {!canManage && (
          <p className="mt-1 flex items-center gap-1 text-sm text-muted-foreground">
            <Users className="size-4" aria-hidden />
            {t('tasting.visit.sharedBy', { name: visit.ownerName })}
          </p>
        )}
      </FeaturePageHeader>

      <div className="space-y-4">
        {visit.place && !choosingPlace ? (
          <>
            <PlaceCard place={visit.place} visit={visit} />
            {canManage && (
              <Button size="sm" variant="ghost" onClick={() => setChoosingPlace(true)}>
                {t('tasting.capture.changePlace')}
              </Button>
            )}
          </>
        ) : canManage ? (
          <section
            className="space-y-3 rounded-lg border p-4"
            aria-labelledby="confirm-place"
            data-testid="confirm-place"
          >
            <h2 id="confirm-place" className="font-semibold">
              {t('tasting.visit.confirmPlace')}
            </h2>
            <p className="text-sm text-muted-foreground">{t('tasting.visit.confirmPlaceHint')}</p>
            <PlacePicker
              position={position}
              accuracyM={visit.accuracyM}
              onSelect={(place) => update.mutate({ placeId: place.id })}
            />
            {visit.place && (
              <Button size="sm" variant="ghost" onClick={() => setChoosingPlace(false)}>
                {t('common.cancel')}
              </Button>
            )}
          </section>
        ) : (
          position && (
            <LazyTastingMap
              ariaLabel={t('tasting.viewMap')}
              points={[{ id: visit.id, ...position, label: '' }]}
              center={position}
              interactive={false}
              className="h-44 w-full overflow-hidden rounded-lg border"
            />
          )
        )}

        {canManage ? (
          <section className="space-y-3 rounded-lg border p-4">
            <div>
              <Label htmlFor={visitedAtId}>{t('tasting.visit.visitedAt')}</Label>
              <Input
                id={visitedAtId}
                type="datetime-local"
                className="mt-1 sm:w-64"
                max={toLocalInput(new Date())}
                value={visitedAt ?? toLocalInput(new Date(visit.visitedAt))}
                onChange={(e) => setVisitedAt(e.target.value)}
                data-testid="visit-date"
              />
              {visitedAt !== null &&
                visitedAt !== '' &&
                visitedAt !== toLocalInput(new Date(visit.visitedAt)) && (
                  <Button
                    size="sm"
                    className="mt-2"
                    loading={update.isPending}
                    disabled={new Date(visitedAt).getTime() > Date.now()}
                    onClick={() => update.mutate({ visitedAt: new Date(visitedAt).toISOString() })}
                    data-testid="visit-date-save"
                  >
                    {t('common.save')}
                  </Button>
                )}
            </div>
            <div>
              <Label htmlFor={commentId}>{t('tasting.visit.comment')}</Label>
              <Textarea
                id={commentId}
                className="mt-1"
                rows={2}
                maxLength={2000}
                value={comment ?? visit.comment ?? ''}
                onChange={(e) => setComment(e.target.value)}
              />
              {comment !== null && comment !== (visit.comment ?? '') && (
                <Button
                  size="sm"
                  className="mt-2"
                  loading={update.isPending}
                  onClick={() => update.mutate({ comment: comment.trim() || null })}
                >
                  {t('common.save')}
                </Button>
              )}
            </div>
            <div className="flex items-start gap-3">
              <Switch
                id={shareId}
                checked={visit.visibility === 'HOUSEHOLD'}
                disabled={update.isPending}
                onCheckedChange={(checked) =>
                  update.mutate({ visibility: checked ? 'HOUSEHOLD' : 'PRIVATE' })
                }
                data-testid="share-visit"
              />
              <div>
                <Label htmlFor={shareId}>{t('tasting.visit.shareLabel')}</Label>
                {/* Sharing exposes the exact position to the household – say so (spec 5.6). */}
                <p className="text-xs text-muted-foreground">{t('tasting.visit.shareHint')}</p>
              </div>
            </div>
          </section>
        ) : (
          visit.comment && <p className="whitespace-pre-wrap text-sm">{visit.comment}</p>
        )}

        <section aria-labelledby="dishes-heading" className="space-y-3">
          <h2 id="dishes-heading" className="text-lg font-semibold">
            {t('tasting.visit.dishes', { count: visit.notes.length })}
          </h2>
          {visit.notes.map((note, index) => (
            <DishCard
              key={note.id}
              visit={visit}
              note={note}
              index={index}
              onChanged={applyVisit}
              onReorder={(from, to) => reorder.mutate({ from, to })}
              reordering={reorder.isPending}
            />
          ))}
        </section>
      </div>

      {merging && (
        <MergeVisitDialog
          visit={visit}
          onClose={() => setMerging(false)}
          onMerged={(merged) => {
            setMerging(false);
            applyVisit(merged);
          }}
        />
      )}
      <ConfirmDialog
        open={deleting}
        onOpenChange={setDeleting}
        title={t('tasting.visit.deleteTitle')}
        description={t('tasting.visit.deleteBody')}
        confirmLabel={t('common.delete')}
        destructive
        loading={remove.isPending}
        onConfirm={() => remove.mutate()}
      />
    </PageContainer>
  );
}
